//@ts-strict-ignore
import { Injectable, Type } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import {LMAdHocSelectedField, LMDialogInfo, LMModel} from '@app/components';

@Injectable()
export class LMOverlayService {

  private _opened = false;
  private _opening = false;
  private _isNavigation = false;
  private _navigationName = '';
  private _resourceId = '';
  private _listOpenedOverlay = '';
  private _url = '';
  private _navigationType : 'list' | 'form' | 'page' | 'adhocSearch' | 'custom' = 'form';
  private _overlayComm$ = new Subject<LMOverlayAction>();
  private _flyoutContentEvent$ = new Subject<LMFlyoutContentEvent>();
  private _component: Type<any> | undefined;
  private _componentInputs: Record<string, any> | undefined;
  private _overlayState$ = new Subject<LMOverlayInfo>();
  private _componentCloseOverlay$ = new Subject<LMCloseOverlayInfo>();
  private _busyIndicator$ = new Subject<boolean>();
  private _openingEvent: any;
  private _routerOutletUnsavedChanges: UnsavedChangesFunction;
  // GenAI Generated code - Start
  // LMWEB-26109: resolver for the IN-FLIGHT navigation guard (runOverlayGuard). Held per guard invocation
  // (not a shared/long-lived Subject) so a report can only ever resolve the one navigation that is waiting,
  // and a stray/duplicate report is ignored. true = user chose to leave (flyout will close), false = stay.
  private _overlayGuardResolve: ((mayLeave: boolean) => void) | null = null;
  // LMWEB-26109: set SYNCHRONOUSLY the moment a close is initiated and cleared when hasOpenOverlay is
  // updated (same deferred step, ~10ms later, in AppComponent.closeFormOverlayDrawer). hasOpenOverlay/_opened
  // is deliberately deferred on close (LMWEB-24203 duplicate-record prevention), so during the synchronous
  // window after a close it still reads true; a navigation guard must consult `closing` to know a close is
  // already in flight and NOT re-fire runOverlayGuard on the DeactivateGuard canDeactivate re-entry.
  private _closing = false;
  // GenAI Generated code - End

  private _backNavigationArray = new Array<LMOverlayInfo>();

  constructor( ) {
    this._componentCloseOverlay$.subscribe((close: LMCloseOverlayInfo) => {
      if (close.from === 'component' && close.canClose) {
        this._opening = false;
        this._listOpenedOverlay = '';
        // GenAI Generated code - Start
        // LMWEB-26109: a real close is now confirmed (the sidebar emits from:'component'/canClose only when
        // the flyout actually closes — not on a "stay"). Mark closing synchronously; hasOpenOverlay/_opened
        // is not updated until ~10ms later (LMWEB-24203, via AppComponent.closeFormOverlayDrawer), so a
        // navigation guard running in that window must consult `closing` to avoid re-firing runOverlayGuard
        // on the DeactivateGuard canDeactivate re-entry. Cleared when hasOpenOverlay is next updated.
        this._closing = true;
        // GenAI Generated code - End
        if (close.openDetails) {
          this._overlayComm$.next( { from: 'overlay', action: 'open' });
          this._overlayState$.next({ open: false, url: '', listOpenedId: '', resourceId: '', isNavigation: false, navigationType: 'form' });
        } else {
          this._overlayState$.next({ open: false, url: '', listOpenedId: '', resourceId: '', isNavigation: false, navigationType: 'form' });
        }
      }
    });
  }

  public set hasOpenOverlay(isOpen: boolean) {
    this._opened = isOpen;
    // GenAI Generated code - Start
    // LMWEB-26109: keep the synchronous `closing` flag in lockstep with the deferred _opened update — once
    // hasOpenOverlay is settled (true on (re)open, false when the close completes), the close is no longer
    // "in flight", so clear the flag.
    this._closing = false;
    // GenAI Generated code - End
  }
  public get hasOpenOverlay(): boolean {
    return this._opened;
  }

  // GenAI Generated code - Start
  // LMWEB-26109: true while a flyout close has been initiated but hasOpenOverlay has not yet been updated
  // (its update is deferred ~10ms for LMWEB-24203). Lets a navigation guard distinguish "flyout genuinely
  // open" from "flyout closing" during that window.
  public get closing(): boolean {
    return this._closing;
  }
  // GenAI Generated code - End

  public get isOpening(): boolean {
    return this._opening;
  }
  public set isOpening(value: boolean) {
    this._opening = value;
  }

  public get listOpenOverlayID(): string {
    return this._listOpenedOverlay;
  }

  public get isNavigation(): boolean {
    return this._isNavigation;
  }

  public get navigationName(): string {
    return this._navigationName;
  }

  public get navigationType(): 'list' | 'form' | 'page' | 'adhocSearch' | 'custom' {
    return this._navigationType;
  }

  public get resourceId(): string {
    return this._resourceId;
  }

  public get overlayComm(): Subject<LMOverlayAction> {
    return this._overlayComm$;
  }

  public get overlayState(): Subject<LMOverlayInfo> {
    return this._overlayState$;
  }

  public get overlayClose(): Subject<LMCloseOverlayInfo> {
    return this._componentCloseOverlay$;
  }

  public get backNavigationArray(): Array<LMOverlayInfo> {
    return this._backNavigationArray;
  }

  public get busyIndicatorSubject(): Subject<boolean> {
    return this._busyIndicator$;
  }

  public get flyoutContentEvent$(): Observable<LMFlyoutContentEvent> {
    return this._flyoutContentEvent$.asObservable();
  }

  public emitFlyoutContentEvent(event: LMFlyoutContentEvent): void {
    this._flyoutContentEvent$.next(event);
  }

  public get openingEvent(): any {
    return this._openingEvent;
  }

  public set openingEvent(event: any) {
    this._openingEvent = event;
  }

  public set routerOutletUnsavedChanges(func: UnsavedChangesFunction) {
    this._routerOutletUnsavedChanges = func;
  }

  public get routerOutletUnsavedChanges(): UnsavedChangesFunction {
    return this._routerOutletUnsavedChanges;
  }

  public open(openFormOverlay: LMOverlayInfo, fromBack = false) {
    if (openFormOverlay.navigationType === 'custom' && !openFormOverlay.component) {
      console.warn('LMOverlayService: navigationType is "custom" but no component was provided.');
      return;
    }
    if (openFormOverlay.navigationType === 'adhocSearch') {
      this._navigationType = openFormOverlay.navigationType;
      this._overlayState$.next(openFormOverlay);

    } else {
      if (!openFormOverlay.insideOverlayNav && !fromBack) {
        // opening on a new record, clear the back history
        this.resetBackNavigation();
      }
      if (this._opened && !fromBack && openFormOverlay.insideOverlayNav) {
        // replacing content, store the current info in the backNavigation array
        const currentInfo: LMOverlayInfo = {
          listOpenedId: this._listOpenedOverlay,
          isNavigation: this._isNavigation,
          navigationName: this._navigationName,
          resourceId: this._resourceId,
          replaceCurrentContent: true,
          open: true,
          url: this._url,
          navigationType: this._navigationType,
          component: this._component,
          componentInputs: this._componentInputs
        };
        this._backNavigationArray.push(currentInfo);
      }
      this._listOpenedOverlay = openFormOverlay.listOpenedId;
      this._isNavigation = openFormOverlay.isNavigation;
      this._navigationType = openFormOverlay.navigationType;
      if (this._isNavigation && openFormOverlay.navigationName) {
        this._navigationName = openFormOverlay.navigationName;
      }
      this._resourceId = openFormOverlay.resourceId;
      this._url = openFormOverlay.url;
      this._component = openFormOverlay.component;
      this._componentInputs = openFormOverlay.componentInputs;
      this._overlayState$.next(openFormOverlay);
    }
  }

  public close(): void {
    if (this.hasOpenOverlay)
      this._componentCloseOverlay$.next({ from: 'service' });
  }

  // GenAI Generated code - Start
  // LMWEB-26109: Navigation guard for an open flyout overlay, modeled on LMDrillAroundService.runDrillAroundGuard.
  // A form inside the flyout is not the owner of page navigation; when the user tries to navigate away (e.g. a
  // breadcrumb click) with a dirty flyout, the single unsaved-changes confirmation is owned by the sidebar
  // (handleUnsavedChangesOnForm, triggered by close()). This guard fires that one confirmation and returns a
  // Promise that resolves once the user answers: true to leave (the sidebar closes the flyout), false to stay.
  // The caller returns this promise from allowPageNavigation so the navigation waits on the user's choice
  // instead of proceeding immediately (and without opening a duplicate dialog).
  public runOverlayGuard(): Promise<boolean> {
    if (!this.hasOpenOverlay) {
      return Promise.resolve(true);
    }
    // If a previous guard is somehow still pending (should not happen — router navigations serialize),
    // release it as "leave" so it can never dangle, then start a fresh one.
    if (this._overlayGuardResolve) {
      this._overlayGuardResolve(true);
      this._overlayGuardResolve = null;
    }
    const result = new Promise<boolean>((resolve) => {
      this._overlayGuardResolve = resolve;
    });
    // close() triggers the sidebar's single unsaved-changes confirmation; its outcome comes back via
    // reportOverlayGuardResult. The subscription (the resolver) is registered above, before close() fires,
    // so the async confirmation result can never be missed.
    this.close();
    return result;
  }

  // Called by the flyout host (LMSidebarResizeComponent) when a close resolves, reporting whether the user
  // chose to leave (true) or stay (false). Every close branch in the sidebar MUST call this so an awaiting
  // runOverlayGuard can never hang: form/list report the confirmation outcome; adhoc/custom/page (which have
  // no unsaved-changes prompt) report true. Idempotent and null-safe: only the in-flight guard is resolved,
  // and a report with no guard waiting (e.g. an X/Escape close unrelated to navigation) is a no-op.
  public reportOverlayGuardResult(mayLeave: boolean): void {
    if (this._overlayGuardResolve) {
      this._overlayGuardResolve(mayLeave);
      this._overlayGuardResolve = null;
    }
  }
  // GenAI Generated code - End

  public back(): void {
    // Since put the info in for every component displayed, need to get the previous off the
    // array, and then remove the last part of it. Should only get here if the array length
    // is at least 2
    if (this._backNavigationArray.length > 0) {
      //remove current content from the array
      const backFormInfo = this._backNavigationArray.pop();
      // const backFormInfo = this._backNavigationArray[this._backNavigationArray.length - 1];
      if (backFormInfo) {
        backFormInfo.replaceCurrentContent = true;
        this.open(backFormInfo, true);
      }
    }
  }
  
  public openFullPage(): void {
    this._componentCloseOverlay$.next({ from: 'service', openDetails: true });
  }

  public resetBackNavigation(): void {
    this._backNavigationArray = [];
  }

  public updateCurrentUrl(url: string): void {
    this._url = url;
  }

  public get hasNavigated(): boolean {
    return this._backNavigationArray.length > 0;
  }

  public busyIndicator(show: boolean): void {
    this._busyIndicator$.next(show);
  }
}

export interface LMOverlayAction {
  action?: string;
  resourceId?: string;
  navigationResourceId?: string;
  from?: 'openingList' | 'overlay' | 'banner' | 'adHocSearch';
  adHocActionInfo?: LMAdHocActionInfo;
}

export interface LMAdHocActionInfo {
  selectedFields?: LMAdHocSelectedField[];
  fcEvent?: LMFilterEvent;
  isReopen?: boolean;
}


export interface LMOverlayInfo {
  open?: boolean;
  url?: string;
  listOpenedId?: string;
  resourceId?: string;
  isNavigation?: boolean;
  navigationType?: 'list' | 'form' | 'page' | 'adhocSearch' | 'custom';
  navigationName?: string;
  replaceCurrentContent?: boolean;
  insideOverlayNav?: boolean;
  updateUrl?: boolean;
  event?: LMFormOverlayClickedEvent;
  component?: Type<any>;
  componentInputs?: Record<string, any>;
  adHocSearchInfo?: LMAdHocSearchInfo;
}

export interface LMAdHocSearchInfo {
  model: LMModel;
  formSpec: LMFormSpec;
  formOptions: LMFormOptions;
  specFields: LMSpecFields;
  layout: LMFormLayout;
  listUrlInfo: LMListUrlInfo;
}

export interface LMCloseOverlayInfo {
  from: 'service' | 'component';
  canClose?: boolean;
  openDetails?: boolean;
}

export interface LMFlyoutContentEvent {
  type: 'columnarAdhocSpec' | 'adhocSearch';
  payload?: any;
}

export interface UnsavedChangesFunction {
  (info: LMDialogInfo): Observable<boolean>;
}

